import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, Clock } from "lucide-react";
import {
  IllusSolar,
  IllusBattery,
  IllusHVAC,
  IllusITE,
  IllusWater,
} from "./Illustrations";

const PHASES = [
  { id: "study", title: "Étude & devis", subtitle: "J-15", body: "Visite, mesures, chiffrage" },
  { id: "prep", title: "Préparation", subtitle: "J-3", body: "Livraison matériel, échafaudage" },
  { id: "install", title: "Installation", subtitle: "J1 → J+", body: "Chantier propre & rapide" },
  { id: "commissioning", title: "Mise en service", subtitle: "Livraison", body: "Contrôles, PV, formation" },
];

const ILLUS_BY = {
  solar: IllusSolar,
  ite: IllusITE,
  hvac: IllusHVAC,
  battery: IllusBattery,
  water: IllusWater,
};

const DURATION_BY = {
  solar: "1 à 3 jours",
  ite: "2 à 4 semaines",
  hvac: "2 à 5 jours",
  battery: "1 jour",
  water: "½ journée",
};

export default function TimelapseTeaser({ solutionId }) {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const Illus = ILLUS_BY[solutionId];
  const totalDuration = DURATION_BY[solutionId] || "quelques jours";

  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => {
      setActive((a) => (a + 1) % PHASES.length);
    }, 2600);
    return () => clearInterval(t);
  }, [playing]);

  if (!Illus) return null;

  return (
    <section className="mt-14 lg:mt-20" data-testid={`timelapse-${solutionId}`}>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eyebrow mb-3">Timelapse · Chantier en accéléré</div>
          <h3 className="font-serif-display text-3xl lg:text-5xl leading-[1] tracking-tight text-[#0F172A] max-w-2xl balance">
            Le chantier étape par étape.
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2 bg-[#F5F1EA] border border-[#e2d9ce] rounded-full px-4 py-2">
            <Clock className="h-4 w-4 text-[#2D7A4D]" />
            <span className="text-sm text-[#0F172A]">
              Durée : <strong>{totalDuration}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Player-like visual */}
      <div className="relative rounded-2xl overflow-hidden border border-[#e2d9ce] bg-[#0F172A]">
        <div className="aspect-[16/8] relative bg-[#0F172A]">
          {/* Rotating illustration frames */}
          <div className="absolute inset-0 flex items-center justify-center p-6 lg:p-10">
            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -10 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-xl aspect-[4/3] rounded-xl overflow-hidden bg-[#FDFBF7] border border-[#e2d9ce]"
            >
              <Illus className="w-full h-full" />
            </motion.div>
          </div>

          {/* Overlay label */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#FF9800] animate-pulse" />
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.24em] text-[#FDFBF7]/80">
              REC · Timelapse HMCD
            </span>
          </div>

          {/* Play/Pause toggle */}
          <button
            data-testid="timelapse-play-toggle"
            onClick={() => setPlaying((p) => !p)}
            className="absolute bottom-4 right-4 h-12 w-12 rounded-full bg-[#FF9800] flex items-center justify-center hover:scale-105 transition-transform"
            aria-label={playing ? "Pause" : "Lire"}
          >
            {playing ? (
              <div className="flex gap-1">
                <span className="w-1 h-4 bg-[#0F172A] rounded-sm" />
                <span className="w-1 h-4 bg-[#0F172A] rounded-sm" />
              </div>
            ) : (
              <Play className="h-5 w-5 text-[#0F172A] ml-0.5" fill="#0F172A" />
            )}
          </button>

          {/* Phase name */}
          <motion.div
            key={`label-${active}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute bottom-4 left-4 max-w-xs"
          >
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#FF9800]">
              {PHASES[active].subtitle}
            </div>
            <div className="font-serif-display text-2xl lg:text-3xl text-[#FDFBF7] leading-tight mt-1">
              {PHASES[active].title}
            </div>
            <div className="text-xs text-[#FDFBF7]/70 mt-1">
              {PHASES[active].body}
            </div>
          </motion.div>
        </div>

        {/* Timeline scrubber */}
        <div className="bg-[#0F172A] border-t border-[#FDFBF7]/10 px-4 py-4">
          <div className="grid grid-cols-4 gap-2">
            {PHASES.map((ph, i) => (
              <button
                key={ph.id}
                onClick={() => {
                  setActive(i);
                  setPlaying(false);
                }}
                data-testid={`timelapse-phase-${i}`}
                className="text-left group"
              >
                <div
                  className={`h-1 rounded-full transition-all ${
                    active === i
                      ? "bg-[#FF9800]"
                      : active > i
                        ? "bg-[#FDFBF7]/60"
                        : "bg-[#FDFBF7]/15"
                  }`}
                />
                <div className="mt-2 font-mono-tech text-[9px] uppercase tracking-[0.2em] text-[#FDFBF7]/60 group-hover:text-[#FDFBF7]">
                  {String(i + 1).padStart(2, "0")} · {ph.title}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
